
import React, { useState } from 'react';
import { format, fromUnixTime } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Courier } from '@/services/types';
import { StatusBadge } from '@/components/StatusBadge';
import { CourierSocialBadges } from './SocialBadges';
import { EditableField } from './EditableField';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Phone,
  Mail,
  Calendar,
  ClipboardList,
  CreditCard,
  MapPin,
  FileText,
  ExternalLink,
  Tag,
  User,
  Hash,
  Database,
  CreditCard as CardIcon,
  Bike,
  Clock,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { updateCourierEmail, updateCourierStatus } from '@/utils/supabase/courierQueries';
import { updateCourierField } from '@/utils/supabase/courier/courierFieldOperations';

interface CourierDetailsCardProps {
  courier: Courier;
}

export const CourierDetailsCard: React.FC<CourierDetailsCardProps> = ({ courier: initialCourier }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [courier, setCourier] = useState(initialCourier);
  
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState(courier.email || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);

  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState<'active' | 'inactive'>(
    courier.status === 'blocked' ? 'inactive' : courier.status as 'active' | 'inactive'
  );
  const [isEditingStatus, setIsEditingStatus] = useState(false);

  const formatUnixDate = (timestamp?: number | null) => {
    if (!timestamp) return 'Не указано';
    try {
      return format(fromUnixTime(timestamp), 'dd.MM.yyyy', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error, timestamp);
      return 'Некорректная дата';
    }
  };

  const openAmocrm = () => {
    if (courier.amo_deal_id) {
      window.open(`https://tuktukrent.amocrm.ru/leads/detail/${courier.amo_deal_id}`, '_blank');
    }
  };

  // Универсальная функция для обновления полей
  const handleFieldUpdate = async (field: string, value: string): Promise<boolean> => {
    try {
      const result = await updateCourierField(courier.id.toString(), field, value);
      
      if (result.ok) {
        setCourier({ ...courier, [field]: value });
        toast.success('Поле успешно обновлено');
        return true;
      } else {
        toast.error('Не удалось обновить поле');
        return false;
      }
    } catch (error) {
      console.error('Ошибка при обновлении поля:', error);
      toast.error('Произошла ошибка при обновлении');
      return false;
    }
  };

  // Валидаторы
  const validateDate = (value: string): string | null => {
    if (value && isNaN(Date.parse(value))) {
      return 'Неверный формат даты';
    }
    return null;
  };

  const validateNumber = (value: string): string | null => {
    if (value && isNaN(Number(value))) {
      return 'Значение должно быть числом';
    }
    return null;
  };

  const handleSaveEmail = async () => {
    if (!email.trim()) {
      toast.error('Email не может быть пустым');
      return;
    }
    
    try {
      setEmailSaving(true);
      
      const response = await updateCourierEmail(courier.id.toString(), email.trim());
      
      if (!response.success) {
        console.error(`Ошибка при обновлении email: `, response.error);
        toast.error('Не удалось обновить email курьера');
        return;
      }
      
      toast.success('Почта обновлена');
      setEditingEmail(false);
      setEmailChanged(false);
      
      setCourier({ ...courier, email });
    } catch (error) {
      console.error('Ошибка при обновлении email:', error);
      toast.error('Произошла ошибка при обновлении email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    setIsStatusUpdating(true);
    
    try {
      const result = await updateCourierStatus(courier.id.toString(), newStatus);
      if (result.error) {
        console.error('Error updating courier status:', result.error);
        toast.error(`Не удалось обновить статус: ${result.error.message || 'Неизвестная ошибка'}`);
      } else {
        setCourier({ ...courier, status: newStatus });
        toast.success('Статус курьера обновлен');
        setIsEditingStatus(false);
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(`Ошибка: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  return (
    <Card className="client-card max-w-full sm:max-w-[800px]">
      <CardContent className="p-0 space-y-5">
        <div className="client-card-section">
          <h3 className="text-base font-medium mb-3 text-foreground/80 flex items-center">
            <User className="h-4 w-4 mr-2 text-tuktuk-teal" />
            Контактная информация
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="client-card-item">
              <Phone className="h-4 w-4 client-card-icon" />
              {isAdmin ? (
                <EditableField
                  value={courier.phone}
                  onSave={(value) => handleFieldUpdate('phone', value)}
                  label="Телефон"
                  placeholder="Введите номер телефона"
                />
              ) : (
                <span className="text-sm">{courier.phone || 'Не указан'}</span>
              )}
            </div>
            <div className="client-card-item">
              <Mail className="h-4 w-4 client-card-icon" />
              {isAdmin && editingEmail ? (
                <form className="flex gap-2 items-center w-full" onSubmit={e => {e.preventDefault(); handleSaveEmail();}}>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setEmailChanged(e.target.value !== (courier.email || ''));
                    }}
                    disabled={emailSaving}
                    className="max-w-xs"
                  />
                  <Button 
                    type="submit" 
                    size="sm" 
                    disabled={!emailChanged || emailSaving}>
                    {emailSaving ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingEmail(false);
                      setEmail(courier.email || '');
                      setEmailChanged(false);
                    }}
                  >
                    ✕
                  </Button>
                </form>
              ) : (
                <span className="text-sm flex items-center gap-2">
                  {courier.email || '-'}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 text-xs"
                      onClick={() => setEditingEmail(true)}
                    >
                      Редактировать
                    </Button>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="client-card-section">
          <h3 className="text-base font-medium mb-3 text-foreground/80 flex items-center">
            <Bike className="h-4 w-4 mr-2 text-tuktuk-teal" />
            Информация об аренде
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="client-card-item">
              <ClipboardList className="h-4 w-4 client-card-icon" />
              <span className="text-sm mr-2">Номер сделки:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.deal_number}
                  onSave={(value) => handleFieldUpdate('deal_number', value)}
                  label="Номер сделки"
                  placeholder="Введите номер сделки"
                />
              ) : (
                <span className="text-sm">{courier.deal_number || '-'}</span>
              )}
            </div>
            
            <div className="client-card-item">
              <Tag className="h-4 w-4 client-card-icon" />
              <span className="text-sm mr-2">Тариф:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.tariff}
                  onSave={(value) => handleFieldUpdate('tariff', value)}
                  label="Тариф"
                  placeholder="Введите тариф"
                />
              ) : (
                <span className="text-sm">{courier.tariff || '-'}</span>
              )}
            </div>
            
            <div className="client-card-item">
              <Calendar className="h-4 w-4 client-card-icon" />
              <span className="text-sm mr-2">Начало:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.rental_start_ts ? formatUnixDate(courier.rental_start_ts) : ''}
                  onSave={async (value) => {
                    const timestamp = value ? Math.floor(new Date(value).getTime() / 1000) : null;
                    return handleFieldUpdate('rental_start_ts', timestamp?.toString() || '');
                  }}
                  label="Дата начала аренды"
                  type="date"
                  validator={validateDate}
                />
              ) : (
                <span className="text-sm">{formatUnixDate(courier.rental_start_ts)}</span>
              )}
            </div>
            
            <div className="client-card-item">
              <Calendar className="h-4 w-4 client-card-icon" />
              <span className="text-sm mr-2">Окончание:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.rental_end_human || (courier.rental_end_ts ? formatUnixDate(courier.rental_end_ts) : '')}
                  onSave={async (value) => {
                    if (value && !isNaN(Date.parse(value))) {
                      const timestamp = Math.floor(new Date(value).getTime() / 1000);
                      return handleFieldUpdate('rental_end_ts', timestamp.toString());
                    } else {
                      return handleFieldUpdate('rental_end_human', value);
                    }
                  }}
                  label="Дата окончания аренды"
                  placeholder="Введите дату окончания"
                />
              ) : (
                <span className="text-sm">{courier.rental_end_human || formatUnixDate(courier.rental_end_ts)}</span>
              )}
            </div>
            
            <div className="client-card-item">
              <CreditCard className="h-4 w-4 client-card-icon" />
              <span className="text-sm mr-2">Последняя оплата:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.last_payment_date}
                  onSave={(value) => handleFieldUpdate('last_payment_date', value)}
                  label="Дата последней оплаты"
                  type="date"
                  validator={validateDate}
                />
              ) : (
                <span className="text-sm">{courier.last_payment_date || 'Нет данных'}</span>
              )}
            </div>
            
            <div className="client-card-item">
              <Clock className="h-4 w-4 client-card-icon" />
              <span className="text-sm">Всего аренд: {courier.totalRentals || 0}</span>
            </div>
          </div>
        </div>
        
        {(courier.amo_deal_id || courier.amo_contact_id) && (
          <div className="client-card-section">
            <h3 className="text-base font-medium mb-3 text-foreground/80 flex items-center">
              <Database className="h-4 w-4 mr-2 text-tuktuk-teal" />
              Данные amoCRM
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {courier.amo_deal_id && (
                <div className="client-card-item">
                  <Database className="h-4 w-4 client-card-icon" />
                  <span className="text-sm">ID сделки: {courier.amo_deal_id}</span>
                </div>
              )}
              
              {courier.amo_contact_id && (
                <div className="client-card-item">
                  <User className="h-4 w-4 client-card-icon" />
                  <span className="text-sm">ID клиента: {courier.amo_contact_id}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mt-2">
          <CourierSocialBadges 
            telegram_id={courier.telegram_id} 
            avito_id={courier.avito_id} 
            vk_id={courier.vk_id}
            phone={courier.phone}
          />
          
          {courier.amo_deal_id && (
            <Button variant="outline" size="sm" className="text-xs" onClick={openAmocrm}>
              <ExternalLink className="h-3 w-3 mr-1" />
              Открыть в amoCRM
            </Button>
          )}
        </div>
        
        <div className="bg-muted/20 border border-border/60 rounded-lg overflow-hidden">
          <div className="bg-muted/30 py-2 px-3">
            <h3 className="text-sm font-medium flex items-center">
              <FileText className="h-4 w-4 mr-2 text-tuktuk-teal" />
              Паспортные данные
            </h3>
          </div>
          <div className="py-3 px-3 space-y-2">
            <div className="client-card-item">
              <Hash className="h-3 w-3 client-card-icon" />
              <span className="text-sm mr-2">Паспорт:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.passport_number}
                  onSave={(value) => handleFieldUpdate('passport_number', value)}
                  label="Номер паспорта"
                  placeholder="Введите номер паспорта"
                />
              ) : (
                <span className="text-sm break-words">{courier.passport_number || '-'}</span>
              )}
            </div>
            <div className="client-card-item">
              <MapPin className="h-3 w-3 client-card-icon" />
              <span className="text-sm mr-2">Адрес:</span>
              {isAdmin ? (
                <EditableField
                  value={courier.registration_address}
                  onSave={(value) => handleFieldUpdate('registration_address', value)}
                  label="Адрес регистрации"
                  placeholder="Введите адрес регистрации"
                  multiline={true}
                />
              ) : (
                <span className="text-sm break-words">{courier.registration_address || '-'}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col gap-3 mt-2">
          {courier.has_paid && (
            <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800/30">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <span className="text-green-700 dark:text-green-400 text-sm">Клиент своевременно совершает оплаты</span>
            </div>
          )}
          
          <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg border border-amber-100 dark:border-amber-900/30">
            <div className="font-medium mb-1 text-amber-800 dark:text-amber-500 text-sm flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              Примечания:
            </div>
            {isAdmin ? (
              <EditableField
                value={courier.comments}
                onSave={(value) => handleFieldUpdate('comments', value)}
                label="Примечания"
                placeholder="Введите примечания"
                multiline={true}
              />
            ) : (
              <div className="text-amber-700 dark:text-amber-400 text-sm break-words">{courier.comments || 'Нет примечаний'}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
